import FullPageLoader from 'Components/FullPageLoader'
import UnauthenticatedLayout from 'Components/Layout/Templates/UnauthenticatedLayout'
import ControlDataManagement from 'Services/ControlDataManagement'
import { AuthorityProvider } from 'Services/common/Features/Authority/Context/authorityContext'
import { LoginPage, logOutUser } from 'Services/common/Features/User'
import {
  canAccessControlDataManagement,
  canAccessProductManagement,
  canAccessSalesManagement,
  canAccessServiceProviderManagement,
  fetchCurrentUser,
  isHslUser
} from 'Services/common/Features/User/userSlice'
import ProductManagement from 'Services/ProductManagement'
import SalesManagement from 'Services/SalesManagement'
import ServiceProviderManagement from 'Services/ServiceProviderManagement'
import useTypedSelector, { useAppDispatch } from 'Store'
import { PreviousLocationProvider } from 'Util/Context/previousLocationContext'
import {
  CONTROL_DATA_MANAGEMENT_ROOT_URL,
  LOGIN_PAGE_PATH,
  PRODUCT_MANAGEMENT_ROOT_URL,
  SALES_MANAGEMENT_ROOT_URL,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import ProtectedRoute from 'Util/ProtectedRoute'
import SnackbarUtils from 'Util/Snackbar'
import { type FC, useEffect } from 'react'
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { t } from 'ttag'

const LoadingView: FC = () => <FullPageLoader />

const App: FC = () => {
  const dispatch = useAppDispatch()
  const { authed: isAuthed, loading, user } = useTypedSelector(state => state.user)

  useEffect(() => {
    dispatch(fetchCurrentUser())
  }, [dispatch])

  const getIndexView = () => {
    if (!user) {
      return <LoadingView />
    }

    if (isHslUser(user)) {
      if (canAccessProductManagement(user)) {
        return <Navigate to={`${PRODUCT_MANAGEMENT_ROOT_URL}/`} />
      } else {
        // The user exists in HSL ID but does not have the required permissions, so log him out
        dispatch(logOutUser()).then(() => {
          SnackbarUtils.error(
            t`The user was logged out because he/she does not have rights to that section.`
          )
        })
        return <Navigate to={LOGIN_PAGE_PATH} />
      }
    }
    return <Navigate to={`${SALES_MANAGEMENT_ROOT_URL}/`} />
  }

  const isLoading = loading === 'pending'

  return (
    <Router>
      <PreviousLocationProvider>
        <Routes>
          <Route path="/" element={isLoading ? <LoadingView /> : <Outlet />}>
            {/* Routes for logged-in users BOF */}
            <Route
              element={
                <AuthorityProvider>
                  <ProtectedRoute
                    isAuthenticated={isAuthed}
                    isAllowed
                    authenticationPath={LOGIN_PAGE_PATH}
                    restrictedPath={'/'}
                  />
                </AuthorityProvider>
              }
              path="/"
            >
              <Route element={getIndexView()} path="/" />

              {/* Sales management route BOF */}
              <Route
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthed}
                    isAllowed={canAccessSalesManagement(user)}
                    authenticationPath={LOGIN_PAGE_PATH}
                    restrictedPath={'/'}
                  />
                }
                path={`${SALES_MANAGEMENT_ROOT_URL}/*`}
              >
                <Route path="*" element={<SalesManagement />} />
              </Route>
              {/* Sales management route EOF */}

              {/* Product management route BOF */}
              <Route
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthed}
                    isAllowed={canAccessProductManagement(user)}
                    authenticationPath={LOGIN_PAGE_PATH}
                    restrictedPath={'/'}
                  />
                }
                path={`${PRODUCT_MANAGEMENT_ROOT_URL}/*`}
              >
                <Route element={<ProductManagement />} path="*" />
              </Route>
              {/* Product management route EOF */}

              {/* Service provider management route BOF */}
              <Route
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthed}
                    isAllowed={canAccessServiceProviderManagement(user)}
                    authenticationPath={LOGIN_PAGE_PATH}
                    restrictedPath={'/'}
                  />
                }
                path={`${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/*`}
              >
                <Route element={<ServiceProviderManagement />} path="*" />
              </Route>
              {/* Service provider management route EOF */}

              {/* Control data management route BOF */}
              <Route
                element={
                  <ProtectedRoute
                    isAuthenticated={isAuthed}
                    isAllowed={canAccessControlDataManagement(user)}
                    authenticationPath={LOGIN_PAGE_PATH}
                    restrictedPath={'/'}
                  />
                }
                path={`${CONTROL_DATA_MANAGEMENT_ROOT_URL}/*`}
              >
                <Route element={<ControlDataManagement />} path="*" />
              </Route>
              {/* Service provider management route EOF */}
            </Route>
            {/* Routes for logged-in users EOF */}

            {/* Login route BOF */}
            <Route element={<UnauthenticatedLayout />} path={LOGIN_PAGE_PATH}>
              <Route index element={<LoginPage />} />
            </Route>
            {/* Login route EOF */}
          </Route>
        </Routes>
      </PreviousLocationProvider>
    </Router>
  )
}
export { App }
export default App
